
import { Component, Vue } from 'vue-property-decorator';
import { PERMISSION } from '@/permissions';
import { Action, Getter } from 'vuex-class';
import Toast from '@/utils/toast';
import ListItem from '@/components/shared/ListItem.vue';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TagEditorModal from '@/components/menu/TagEditorModal.vue';
import ModalWrapper from '@/components/shared/ModalWrapper.vue';

const namespace = 'tags';

@Component({
	components: {
		ListItem,
		ConfirmationModal,
		TextButton,
		TagEditorModal,
		ModalWrapper
	}
})
export default class TagsManager extends Vue {
	@Action('fetchTags', { namespace }) private fetchTags!: () => Promise<void>;
	@Action('deleteTag', { namespace }) private deleteTag!: (tagName: string) => Promise<void>;
	@Getter('getAllTags', { namespace }) private tags!: MenuItemTag[];
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;

	private PERMISSION = PERMISSION;
	private showCreateTagDialog: boolean = false;
	private showDeleteModal: boolean = false;
	private loading: boolean = false;
	private editingTag: MenuItemTag | null = null;
	private tagToDelete: { name: string } | null = null;
	private selectedLocale: RestaurantLocale | null = null;

	private get formattedTags(): FormattedTag[] {
		return this.tags.map(tag => ({
			id: tag.name,
			name: `<span class="tag-chip tag-preview label-md" style="color: ${tag.color || '#666'} !important; "><i class="v-icon mdi ${tag.icon || 'mdi-tag'}" style="margin-right: 8px; font-size: 20px; opacity: 1 !important;"></i>${this.$options.filters!.localize(tag, 'name')}</span>`,
			tagName: tag.name,
			isTag: true,
			published_at: true
		}));
	}

	private get actionsArray(): ActionItem[] {
		if (!this.hasPermission(PERMISSION.CREATE_TAGS)) {
			return [];
		}

		return [
			{
				iconName: 'mdi-pencil',
				buttonLabel: this.$t('shared.actions_array.btn_edit'),
				onClick: (evt: object, item: any): void => {
					this.editTag(item);
				}
			},
			{
				iconName: 'mdi-delete',
				buttonLabel: this.$t('shared.actions_array.btn_delete'),
				onClick: (evt: object, item: any): void => {
					this.deleteTagAction(item);
				}
			}
		];
	}

	private async mounted(): Promise<void> {
		await this.fetchTags()
			.catch((errorMessage) => {
				Toast.error(this.$t('menu.tags.errors.error_fetching', { errorMessage }));
			});
	}

	/**
	 * Opens the tag editor modal with the selected tag's data for editing
	 *
	 * @param {FormattedTag} item
	 * @return {void}
	 */
	private editTag(item: FormattedTag): void {
		const tag = this.tags.find(t => t.name === item.tagName);
		if (!tag) {
			return;
		}

		this.editingTag = tag;
		this.showCreateTagDialog = true;
	}

	/**
	 * Closes the tag editor modal and clears the editing state
	 *
	 * @return {void}
	 */
	private closeTagEditor(): void {
		this.showCreateTagDialog = false;
		this.editingTag = null;
		this.selectedLocale = null;
	}

	/**
	 * Opens the delete confirmation modal for the selected tag
	 *
	 * @param {FormattedTag} item
	 * @return {void}
	 */
	private deleteTagAction(item: FormattedTag): void {
		this.tagToDelete = { name: item.tagName };
		this.showDeleteModal = true;
	}

	/**
	 * Dismisses the delete confirmation modal without deleting
	 *
	 * @return {void}
	 */
	private cancelDelete(): void {
		this.showDeleteModal = false;
		this.tagToDelete = null;
	}

	/**
	 * Deletes the tag via the store action and closes the modal on success
	 *
	 * @return {Promise<void>}
	 */
	private async confirmDelete(): Promise<void> {
		if (!this.tagToDelete) {
			return;
		}

		const tagName = this.tagToDelete.name;
		this.loading = true;

		try {
			await this.deleteTag(tagName);
			this.showDeleteModal = false;
			this.tagToDelete = null;
		}
		catch (errorMessage) {
			Toast.error(this.$t('menu.tags.errors.error_deleting', { errorMessage }));
		}
		finally {
			this.loading = false;
		}
	}
}
